/**
 * Shared CSV/Excel export utility for all financial modules.
 * Generates a properly formatted CSV and triggers download.
 *
 * Preview-before-Download (2026-09-14): every `exportXxx(rows)` function below now has a
 * matching `buildXxxRows(rows)` that returns the same `{ headers, matrix }` shape without
 * downloading anything — this is what powers the shared `<CsvActions>` component's "Preview"
 * button (a table modal of the exact same data before committing to a file). `exportXxx` is
 * kept as a thin wrapper over `buildXxxRows` + `downloadCsv` so any old call site that still
 * calls it directly keeps working unchanged.
 */

function escapeCsvValue(val) {
  if (val == null) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Turns rows into a plain {headers, matrix} shape — the shared currency between Preview and Download. */
export function buildRows(headers, rows, accessors) {
  return { headers, matrix: rows.map(row => accessors.map(fn => fn(row))) };
}

/** Actually triggers the file save, given an already-built {headers, matrix}. */
export function downloadCsv(filename, headers, matrix) {
  const headerLine = headers.map(escapeCsvValue).join(',');
  const dataLines = matrix.map(row => row.map(escapeCsvValue).join(','));
  const csv = [headerLine, ...dataLines].join('\n');

  const BOM = '﻿';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export data to CSV file directly (old call sites still use this).
 * @param {string} filename - Name for the downloaded file (without extension)
 * @param {string[]} headers - Column headers
 * @param {Array<Object>} rows - Data rows
 * @param {Function[]} accessors - Array of functions, each takes a row and returns the cell value
 */
export function exportToCsv(filename, headers, rows, accessors) {
  const { matrix } = buildRows(headers, rows, accessors);
  downloadCsv(filename, headers, matrix);
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';
const fmtEnum = (v) => v ? v.replace(/_/g, ' ') : '';

const INVOICE_HEADERS = ['Invoice #', 'Customer', 'Date', 'Due Date', 'Currency', 'Total', 'Balance Due', 'Status'];
const INVOICE_ACCESSORS = [
  r => r.invoiceNumber, r => r.customerName, r => fmtDate(r.invoiceDate), r => fmtDate(r.dueDate),
  r => r.currency || 'PKR', r => r.totalAmount, r => r.balanceDue, r => r.status,
];
export function buildInvoiceRows(invoices) { return buildRows(INVOICE_HEADERS, invoices, INVOICE_ACCESSORS); }
export function exportInvoices(invoices) { const { headers, matrix } = buildInvoiceRows(invoices); downloadCsv('invoices', headers, matrix); }

const CUSTOMER_HEADERS = ['Code', 'Name', 'Type', 'Email', 'Phone', 'City', 'Country', 'Credit Limit (PKR)', 'Payment Terms', 'Contact Person', 'Active'];
const CUSTOMER_ACCESSORS = [
  r => r.customerCode, r => r.name, r => r.customerType, r => r.email, r => r.phone, r => r.city, r => r.country,
  r => r.creditLimit, r => r.paymentTerms ? `${r.paymentTerms} days` : '', r => r.contactPerson, r => r.active !== false ? 'Yes' : 'No',
];
export function buildCustomerRows(customers) { return buildRows(CUSTOMER_HEADERS, customers, CUSTOMER_ACCESSORS); }
export function exportCustomers(customers) { const { headers, matrix } = buildCustomerRows(customers); downloadCsv('customers', headers, matrix); }

const CASH_TX_HEADERS = ['Transaction #', 'Type', 'Date', 'Amount', 'Currency', 'Party', 'Party Type', 'Category', 'Description', 'Reference', 'Linked Invoice', 'Linked GD'];
const CASH_TX_ACCESSORS = [
  r => r.transactionNumber, r => fmtEnum(r.type), r => fmtDate(r.transactionDate), r => r.amount, r => r.currency || 'PKR',
  r => r.counterpartyName, r => fmtEnum(r.counterpartyType), r => fmtEnum(r.category), r => r.description, r => r.referenceNumber,
  r => r.linkedInvoiceNumber || '', r => r.linkedGDNumber || '',
];
export function buildCashTransactionRows(transactions) { return buildRows(CASH_TX_HEADERS, transactions, CASH_TX_ACCESSORS); }
export function exportCashTransactions(transactions) { const { headers, matrix } = buildCashTransactionRows(transactions); downloadCsv('cash_transactions', headers, matrix); }

const CHECK_HEADERS = ['Check #', 'Type', 'Status', 'Bank', 'Account', 'Amount', 'Currency', 'Party', 'Issue Date', 'Due Date', 'Cleared', 'Purpose', 'Linked Invoice', 'Linked GD'];
const CHECK_ACCESSORS = [
  r => r.checkNumber, r => fmtEnum(r.checkType), r => fmtEnum(r.status), r => r.bankName, r => r.accountNumber, r => r.amount,
  r => r.currency || 'PKR', r => r.partyName, r => fmtDate(r.issueDate), r => fmtDate(r.dueDate), r => fmtDate(r.clearanceDate),
  r => r.purpose, r => r.linkedInvoiceNumber || '', r => r.linkedGDNumber || '',
];
export function buildCheckRows(checks) { return buildRows(CHECK_HEADERS, checks, CHECK_ACCESSORS); }
export function exportChecks(checks) { const { headers, matrix } = buildCheckRows(checks); downloadCsv('check_register', headers, matrix); }

const PAY_ORDER_HEADERS = ['PO Number', 'Status', 'Bank', 'Amount', 'Currency', 'Beneficiary', 'Beneficiary Bank', 'Issue Date', 'Purpose', 'Linked Invoice', 'Linked GD'];
const PAY_ORDER_ACCESSORS = [
  r => r.payOrderNumber, r => fmtEnum(r.status), r => r.bankName, r => r.amount, r => r.currency || 'PKR', r => r.beneficiaryName,
  r => r.beneficiaryBank, r => fmtDate(r.issueDate), r => r.purpose, r => r.linkedInvoiceNumber || '', r => r.linkedGDNumber || '',
];
export function buildPayOrderRows(payOrders) { return buildRows(PAY_ORDER_HEADERS, payOrders, PAY_ORDER_ACCESSORS); }
export function exportPayOrders(payOrders) { const { headers, matrix } = buildPayOrderRows(payOrders); downloadCsv('pay_orders', headers, matrix); }

const GD_HEADERS = ['GD Number', 'Consignee', 'Country', 'Invoice Value (USD)', 'Filing Date', 'Settlement Deadline', 'Status', 'Bank', 'Exchange Rate', 'Net PKR Received', 'Port of Loading', 'Destination'];
const GD_ACCESSORS = [
  r => r.gdNumber, r => r.consigneeName, r => r.consigneeCountry, r => r.invoiceValue, r => fmtDate(r.filingDate),
  r => fmtDate(r.settlementDeadline), r => fmtEnum(r.status), r => r.bankName, r => r.exchangeRate, r => r.netPkrReceived,
  r => r.portOfLoading, r => r.portOfDestination,
];
export function buildGDRows(gdList) { return buildRows(GD_HEADERS, gdList, GD_ACCESSORS); }
export function exportGDs(gdList) { const { headers, matrix } = buildGDRows(gdList); downloadCsv('gd_register', headers, matrix); }

const EXPENSE_HEADERS = ['Expense #', 'Title', 'Category', 'Date', 'Amount', 'Currency', 'Status', 'Submitted By', 'Vendor', 'Payment Method', 'Notes'];
const EXPENSE_ACCESSORS = [
  r => r.expenseNumber, r => r.title, r => fmtEnum(r.category), r => fmtDate(r.expenseDate), r => r.amount, r => r.currency || 'PKR',
  r => fmtEnum(r.status), r => r.submittedByName, r => r.vendorName, r => fmtEnum(r.paymentMethod), r => r.notes,
];
export function buildExpenseRows(expenses) { return buildRows(EXPENSE_HEADERS, expenses, EXPENSE_ACCESSORS); }
export function exportExpenses(expenses) { const { headers, matrix } = buildExpenseRows(expenses); downloadCsv('expenses', headers, matrix); }

const PURCHASE_HEADERS = ['PO #', 'Vendor', 'Date', 'Due Date', 'Currency', 'Total', 'Paid', 'Outstanding', 'Status', 'Linked Gate Pass', 'Vendor Invoice #', 'Reference'];
const PURCHASE_ACCESSORS = [
  r => r.purchaseNumber, r => r.vendorName, r => fmtDate(r.purchaseDate), r => fmtDate(r.dueDate), r => r.currency || 'PKR',
  r => r.totalAmount, r => r.amountPaid, r => r.outstandingAmount, r => fmtEnum(r.status), r => r.linkedGatePassNumber || '',
  r => r.vendorInvoiceNumber || '', r => r.referenceNumber || '',
];
export function buildPurchaseRows(purchases) { return buildRows(PURCHASE_HEADERS, purchases, PURCHASE_ACCESSORS); }
export function exportPurchases(purchases) { const { headers, matrix } = buildPurchaseRows(purchases); downloadCsv('purchases', headers, matrix); }

const GATE_PASS_HEADERS = ['GP #', 'Type', 'Status', 'Date', 'Party', 'Warehouse', 'Vehicle/Container', 'Total Qty', 'Total Weight', 'Batch', 'Remarks'];
const GATE_PASS_ACCESSORS = [
  r => r.gatePassNumber, r => fmtEnum(r.type), r => fmtEnum(r.status), r => fmtDate(r.gatePassDate), r => r.partyName,
  r => r.godownName, r => r.vehicleNumber, r => r.totalQuantity, r => r.totalWeight, r => r.batchCode || '', r => r.remarks || '',
];
export function buildGatePassRows(gatePasses) { return buildRows(GATE_PASS_HEADERS, gatePasses, GATE_PASS_ACCESSORS); }
export function exportGatePasses(gatePasses) { const { headers, matrix } = buildGatePassRows(gatePasses); downloadCsv('gate_passes', headers, matrix); }

const PRODUCT_HEADERS = ['Code', 'Name', 'Category', 'Unit', 'Pack Size', 'Current Stock', 'Reorder Level', 'Default Sale Price', 'Last Purchase Price', 'Primary Vendor', 'Active'];
const PRODUCT_ACCESSORS = [
  r => r.productCode, r => r.name, r => fmtEnum(r.category), r => r.unit, r => r.packSize, r => r.currentStock, r => r.reorderLevel,
  r => r.defaultSalePrice, r => r.lastPurchasePrice, r => r.primaryVendorName || '', r => r.active !== false ? 'Yes' : 'No',
];
export function buildProductRows(products) { return buildRows(PRODUCT_HEADERS, products, PRODUCT_ACCESSORS); }
export function exportProducts(products) { const { headers, matrix } = buildProductRows(products); downloadCsv('product_catalog', headers, matrix); }
